import { OptionGroup } from "@pushpress/shared-components/build/components/inputs/RichInput/RichInput.types";
import _ from "lodash";
import { AppointmentType } from "../../../apis/appointmentApi";
import {
  AppointmentTypeForm,
  MemberStatus,
  TimingOption,
} from "./AppointmentTypeDrawer.types";
import { AppointmentTypeItemFragment } from "./AppointmentTypeDrawer-generated.hooks";

export const AVAILABILITY_START_DEFAULT = "9:00 AM";

export const AVAILABILITY_END_DEFAULT = "5:00 PM";

// PLAT-1106 booking length options 5, 10, 15, 30, 45, 60, 90
export const scheduleMinuteOptions = minuteOptions(5, 3, 5)
  .concat(minuteOptions(15, 3, 30))
  .concat(minuteOptions(0, 1, 90));

export const bufferOptions = createMinuteOptions(
  [5, 10, 15, 30, 45, 60],
  "No buffer",
  0,
);

export const memberStatusOptions: {
  label: string;
  value: MemberStatus;
}[] = [
  { value: "member", label: "Member" },
  { value: "non-member", label: "Non-member" },
  { value: "lead", label: "Lead" },
];

export const mixedScheduleOptions = [
  {
    value: 0,
    label: "0 mins before",
  },
  {
    value: 15 * 60,
    label: "15 mins before",
  },
  {
    value: 30 * 60,
    label: "30 mins before",
  },
  {
    value: 45 * 60,
    label: "45 mins before",
  },
  {
    value: 60 * 60,
    label: "1 hour before",
  },
  {
    value: 2 * 60 * 60,
    label: "2 hours before",
  },
  {
    value: 3 * 60 * 60,
    label: "3 hours before",
  },
  {
    value: 4 * 60 * 60,
    label: "4 hours before",
  },
  {
    value: 6 * 60 * 60,
    label: "6 hours before",
  },
  {
    value: 8 * 60 * 60,
    label: "8 hours before",
  },
  {
    value: 12 * 60 * 60,
    label: "12 hours before",
  },
  {
    value: 16 * 60 * 60,
    label: "16 hours before",
  },
  {
    value: 24 * 60 * 60,
    label: "1 day before",
  },
  {
    value: 2 * 24 * 60 * 60,
    label: "2 days before",
  },
];

export const timingOptions = [
  { value: "minute", label: "minutes before" },
  { value: "hour", label: "hours before" },
  { value: "day", label: "days before" },
];

const defaultSubject = "{{user.first_name}} and {{appointment.name}} ";

const bookingMessage = `<p>Hi {{user.first_name}},</p>
<p>Your {{appointment.name}} appointment with {{staff.first_name}} has been confirmed for {{appointment.time}} on {{appointment.date}}.<p>`;

const reminderMessage = `<p>You have an upcoming appointment for {{appointment.name}} on {{appointment.date}} at {{appointment.time}}.<p>`;

export const appointmentTypeFormDefault: Partial<AppointmentTypeForm> = {
  color: "#0063ff",
  duration: 60,
  availability: [
    {
      checked: false,
      dayOfTheWeek: "Sunday",
    },
    {
      checked: true,
      dayOfTheWeek: "Monday",
      startTime: AVAILABILITY_START_DEFAULT,
      endTime: AVAILABILITY_END_DEFAULT,
    },
    {
      checked: true,
      dayOfTheWeek: "Tuesday",
      startTime: AVAILABILITY_START_DEFAULT,
      endTime: AVAILABILITY_END_DEFAULT,
    },
    {
      checked: true,
      dayOfTheWeek: "Wednesday",
      startTime: AVAILABILITY_START_DEFAULT,
      endTime: AVAILABILITY_END_DEFAULT,
    },
    {
      checked: true,
      dayOfTheWeek: "Thursday",
      startTime: AVAILABILITY_START_DEFAULT,
      endTime: AVAILABILITY_END_DEFAULT,
    },
    {
      checked: true,
      dayOfTheWeek: "Friday",
      startTime: AVAILABILITY_START_DEFAULT,
      endTime: AVAILABILITY_END_DEFAULT,
    },
    {
      checked: false,
      dayOfTheWeek: "Saturday",
    },
  ],
  minimumSchedulingNotice: mixedScheduleOptions.find((o) => o.value === 7200),
  bufferBeforeAppointment: bufferOptions.find((o) => o.value === 0),
  bufferAfterAppointment: bufferOptions.find((o) => o.value === 0),
  availabilityIncrements: scheduleMinuteOptions.find((o) => o.value === 1800),
  lateCancel: mixedScheduleOptions.find((o) => o.value === 14400),
  emailNotificationSubject: defaultSubject,
  message: bookingMessage,
  emailReminderSubject: defaultSubject,
  emailReminderMessage: reminderMessage,
  textReminderMessage: reminderMessage,
  notifyStaffMember: true,
  sendEmailNotification: true,
  sendEmailReminder: true,
  sendTextReminder: true,
  textReminderTimingValue: 1,
  textReminderTimingUnit: timingOptions.find(
    (o) => o.value === "hour",
  ) as TimingOption,
  emailReminderTimingValue: 24,
  emailReminderTimingUnit: timingOptions.find(
    (o) => o.value === "hour",
  ) as TimingOption,
};

function createMinuteOptions(
  arr: number[],
  emptyLabel: string,
  emptyValue: number,
) {
  const emptyOption = {
    value: emptyValue,
    label: emptyLabel,
  };
  const options = arr.map((nu) => {
    return {
      value: nu * 60,
      label: `${nu} min`,
    };
  });
  return [emptyOption, ...options];
}

function minuteOptions(increment: number, length: number, start = 0) {
  const result: { value: number; label: string }[] = [];
  let cur = start;
  for (let i = 0; i < length; i++) {
    result.push({
      value: cur * 60,
      label: `${cur} min`,
    });
    cur += increment;
  }
  return result;
}

export function convertToAppointmentTypeForm(
  appointmentType?: AppointmentTypeItemFragment,
  duplicate?: boolean,
): AppointmentTypeForm | undefined {
  if (!appointmentType) {
    return;
  }

  const name = duplicate
    ? `${appointmentType.name} copy`
    : appointmentType.name;
  const emailReminderTimingOption = convertSecondsToLargestWholeUnit(
    appointmentType.emailReminderTiming,
  );
  const textReminderTimingOption = convertSecondsToLargestWholeUnit(
    appointmentType.textReminderTiming,
  );
  const appointmentStaff = appointmentType.staff;

  return {
    // details
    name,
    color: appointmentType.color,
    duration: appointmentType.duration / 60,
    staff: appointmentStaff
      .filter((item) => !!item)
      .map((item) => ({
        userUuid: item.userUuid,
        firstName: item.firstName || undefined,
        lastName: item.lastName || undefined,
      })),
    availability: appointmentTypeFormDefault.availability.map((item) => {
      const existent = appointmentType.availability.find(
        (i) => i.dayOfTheWeek === item.dayOfTheWeek,
      );
      if (existent) {
        return {
          ...existent,
          checked: true,
        };
      } else {
        return {
          dayOfTheWeek: item.dayOfTheWeek,
          checked: false,
        };
      }
    }),

    // settings
    minimumSchedulingNotice: mixedScheduleOptions.find(
      (o) => o.value === Number(appointmentType.minimumSchedulingNotice),
    ),
    bufferBeforeAppointment: bufferOptions.find(
      (o) => o.value === Number(appointmentType.bufferBeforeAppointment),
    ),
    bufferAfterAppointment: bufferOptions.find(
      (o) => o.value === Number(appointmentType.bufferAfterAppointment),
    ),
    availabilityIncrements: scheduleMinuteOptions.find(
      (o) => o.value === Number(appointmentType.availabilityIncrements),
    ),
    hasNextSteps: appointmentType.hasNextSteps,
    nextStepsLabel: appointmentType.nextStepsLabel,
    nextStepsUrl: appointmentType.nextStepsUrl,
    lateCancel: mixedScheduleOptions.find(
      (o) => o.value === Number(appointmentType.lateCancel),
    ),

    memberStatus: memberStatusOptions.find(
      (memberStatus) =>
        memberStatus.value === appointmentType.memberStatus ?? "non-member",
    ),

    // notifications
    sendEmailNotification: appointmentType.sendEmailNotification,
    emailNotificationSubject: appointmentType.emailNotificationSubject,
    message: appointmentType.message,
    notifyStaffMember: appointmentType.notifyStaffMember,

    sendEmailReminder: appointmentType.sendEmailReminder,
    emailReminderSubject: appointmentType.emailReminderSubject,
    emailReminderTimingValue: emailReminderTimingOption.value,
    emailReminderTimingUnit: emailReminderTimingOption.option,
    emailReminderMessage: appointmentType.emailReminderMessage,

    sendTextReminder: appointmentType.sendTextReminder,
    textReminderTimingValue: textReminderTimingOption.value,
    textReminderTimingUnit: textReminderTimingOption.option,
    textReminderMessage: appointmentType.textReminderMessage,
  };
}

export function convertSecondsToLargestWholeUnit(secs: number | null) {
  if (secs === null) {
    return {
      value: 1,
      option: timingOptions.find((o) => o.value === "hour"),
    };
  }
  const hourInMins = 60;
  const dayInMins = hourInMins * 24;
  const secsValue = secs;
  const mins = secsValue / 60;
  if (mins % dayInMins === 0) {
    const value = mins / dayInMins;
    return {
      value,
      option: timingOptions.find((o) => o.value === "day"),
    };
  }
  if (mins % hourInMins === 0) {
    const value = mins / hourInMins;
    return {
      value,
      option: timingOptions.find((o) => o.value === "hour"),
    };
  }
  return {
    value: mins,
    option: timingOptions.find((o) => o.value === "minute"),
  };
}

export function convertToAppointmentType(form?: AppointmentTypeForm) {
  if (!form) {
    return;
  }

  return {
    // details
    name: form.name,
    color: form.color,
    duration: form.duration * 60,
    staff: form.staff.map((item) => ({
      uuid: item.userUuid,
    })),
    availability: form.availability
      .filter((item) => item.checked)
      .map((props) => _.omit(props, "checked", "__typename")),

    // settings
    minimumSchedulingNotice: form.minimumSchedulingNotice.value,
    bufferBeforeAppointment: _.isNumber(form.bufferBeforeAppointment.value)
      ? String(form.bufferBeforeAppointment.value)
      : undefined,
    bufferAfterAppointment: _.isNumber(form.bufferAfterAppointment.value)
      ? String(form.bufferAfterAppointment.value)
      : undefined,
    availabilityIncrements: String(form.availabilityIncrements.value),
    hasNextSteps: form.hasNextSteps,
    nextStepsLabel: form.hasNextSteps ? form.nextStepsLabel : "",
    nextStepsUrl: form.hasNextSteps ? form.nextStepsUrl : "",
    lateCancel: form.lateCancel.value,
    memberStatus: form.memberStatus?.value,

    // notifications
    sendEmailNotification: form.sendEmailNotification,
    emailNotificationSubject: form.sendEmailNotification
      ? form.emailNotificationSubject
      : defaultSubject,
    message: form.sendEmailNotification ? form.message : bookingMessage,
    sendEmailReminder: form.sendEmailReminder,
    emailReminderSubject: form.sendEmailReminder
      ? form.emailReminderSubject
      : defaultSubject,
    emailReminderTiming: form.sendEmailReminder
      ? convertToSeconds(
          form.emailReminderTimingValue,
          form.emailReminderTimingUnit?.value,
        )
      : 86400,
    emailReminderMessage: form.sendEmailReminder
      ? form.emailReminderMessage
      : reminderMessage,
    notifyStaffMember: form.notifyStaffMember,
    sendTextReminder: form.sendTextReminder,
    textReminderTiming: form.sendTextReminder
      ? convertToSeconds(
          form.textReminderTimingValue,
          form.textReminderTimingUnit?.value,
        )
      : 3600,
    textReminderMessage: form.sendTextReminder
      ? form.textReminderMessage
      : reminderMessage,
  } as AppointmentType;
}

function convertToSeconds(value, option) {
  const time = parseInt(value, 10);
  switch (option) {
    case "minute":
    case "minutes":
      return time * 60;
    case "hour":
    case "hours":
      return time * 3600;
    case "day":
    case "days":
      return time * 86400;
    case "week":
    case "weeks":
      return time * 604800;
    case "month":
    case "months":
      return time * 2592000;
    default:
      return value;
  }
}

export const smartTextOptions: OptionGroup[] = [
  {
    label: "Appointment",
    options: [
      {
        value: "appointment.name",
        label: "Name",
      },
      {
        value: "appointment.date",
        label: "Date",
      },
      {
        value: "appointment.time",
        label: "Time",
      },
    ],
  },
  {
    label: "Client",
    options: [
      {
        value: "user.email",
        label: "Email",
      },
      {
        value: "user.first_name",
        label: "First name",
      },
      {
        value: "user.last_name",
        label: "Last name",
      },
    ],
  },
  {
    label: "Staff",
    options: [
      {
        value: "staff.email",
        label: "Email",
      },
      {
        value: "staff.first_name",
        label: "First name",
      },
      {
        value: "staff.last_name",
        label: "Last name",
      },
    ],
  },
  {
    label: "Business",
    options: [
      {
        value: "client.company",
        label: "Name",
      },
      {
        value: "client.email",
        label: "Email",
      },
      {
        value: "client.phone",
        label: "Phone",
      },
      {
        value: "client.address",
        label: "Address",
      },
    ],
  },
];

export function addDefaultValues(values: AppointmentTypeForm) {
  const nextValues = { ...values };

  if (!nextValues.emailNotificationSubject) {
    nextValues.emailNotificationSubject = defaultSubject;
  }
  if (!nextValues.message) {
    nextValues.message = bookingMessage;
  }

  if (!nextValues.emailReminderSubject) {
    nextValues.emailReminderSubject = defaultSubject;
  }
  if (!nextValues.emailReminderMessage) {
    nextValues.emailReminderMessage = reminderMessage;
  }

  if (!nextValues.textReminderMessage) {
    nextValues.textReminderMessage = reminderMessage;
  }

  if (!nextValues.textReminderTimingValue) {
    nextValues.textReminderTimingValue = 1;
    nextValues.textReminderTimingUnit = timingOptions.find(
      (o) => o.value === "hour",
    ) as TimingOption;
  }

  if (!nextValues.emailReminderTimingValue) {
    nextValues.emailReminderTimingValue = 24;
    nextValues.emailReminderTimingUnit = timingOptions.find(
      (o) => o.value === "hour",
    ) as TimingOption;
  }

  return nextValues;
}
